/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import React from 'react';
import { decorate, computed, action } from 'mobx';
import { observer, inject } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Container, Icon } from 'semantic-ui-react';
import { gotoFn } from '@amzn/base-ui/dist/helpers/routing';

import StudySelectionGrid from './StudySelectionGrid';
import StudyStepsProgress from './StudyStepsProgress';
import FloatingNavBar from '../environments-sc/setup/FloatingNavBar';
import StepGuideCard from '../helpers/StepGuideCard';

const iconStyle = { lineHeight: 1, height: '1em', margin: 0 };

// expected props
// - filesSelection (via injection)
class StudySelectionPage extends React.Component {
  constructor(props) {
    super(props);
    this.topBarRef = React.createRef();
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    // Selection is restored from localStorage by StudySelectionGrid.applyAutoSelection,
    // so we no longer clear it here — that was wiping out the auto-selection.
  }

  goto(pathname) {
    const goto = gotoFn(this);
    goto(pathname);
  }

  get selection() {
    return this.props.filesSelection;
  }

  handleNext = () => {
    // If an envTypeId was passed (e.g. from test launch), forward it to skip type selection
    const params = new URLSearchParams(this.props.location.search);
    const envTypeId = params.get('envTypeId');
    if (envTypeId) {
      this.goto(`/workspaces/setup/type/${encodeURIComponent(envTypeId)}`);
    } else {
      this.goto('/workspaces/setup');
    }
  };

  handleCancel = () => {
    this.props.filesSelection.cleanup();
    this.goto('/workspaces');
  };

  render() {
    return (
      <Container className="mt3">
        {this.renderStepsProgress()}
        {this.renderTopBar()}
        {this.renderGuideCard()}
        {this.renderFloatingNav()}
        <StudySelectionGrid />
      </Container>
    );
  }

  renderStepsProgress() {
    // Pass no currentStep — the stepper will default to step 0 (Select Studies)
    return <StudyStepsProgress />;
  }

  renderTopBar() {
    const empty = this.selection.empty;

    return (
      <div className="setup-top-bar" ref={this.topBarRef}>
        <div className="setup-top-bar-left">
          <span className="setup-top-bar-title">
            <Icon name="database" style={iconStyle} /> Select Studies
          </span>
        </div>
        <div className="setup-top-bar-nav">
          <button
            type="button"
            className="workspace-action-btn workspace-action-btn-secondary"
            onClick={this.handleCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="workspace-action-btn workspace-action-btn-primary"
            onClick={this.handleNext}
          >
            {empty ? 'Skip' : 'Next'} <Icon name="right arrow" style={iconStyle} />
          </button>
        </div>
        <div className="setup-top-bar-description">
          Choose the data studies and/or licenses you would like to mount to your workspace. Note, you cannot attach these later without recreating the workspace.
        </div>
      </div>
    );
  }

  renderGuideCard() {
    return (
      <StepGuideCard guideId="step-select-studies" title="Mounting Studies">
        On this page, you can select any data studies or software licenses you would like
        mounted to your workspace. Your personal and shared studies are shown below. Simply
        click on a study to select it. If you don&apos;t need any right now you can skip this
        step, however please note that studies cannot be attached after creation. We recommend
        selecting your personal and shared studies just in case.
      </StepGuideCard>
    );
  }

  renderFloatingNav() {
    const empty = this.selection.empty;

    return (
      <FloatingNavBar sentinelRef={this.topBarRef}>
        <button
          type="button"
          className="workspace-action-btn workspace-action-btn-secondary"
          onClick={this.handleCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="workspace-action-btn workspace-action-btn-primary"
          onClick={this.handleNext}
        >
          {empty ? 'Skip' : 'Next'} <Icon name="right arrow" style={iconStyle} />
        </button>
      </FloatingNavBar>
    );
  }
}

decorate(StudySelectionPage, {
  selection: computed,
  handleNext: action,
  handleCancel: action,
});

export default inject('filesSelection')(withRouter(observer(StudySelectionPage)));
